import { supabase } from '../lib/supabase';

export const ayurvedaService = {
  // Export supabase client for validation
  supabase,

  // Treatment Categories
  async getTreatmentCategories() {
    try {
      const { data, error } = await supabase?.from('treatment_categories')?.select('*')?.order('sort_order', { ascending: true });

      if (error) throw error;
      return { data, error: null };
    } catch (error) {
      return { data: null, error };
    }
  },

  // Treatments
  async getTreatments(filters = {}) {
    try {
      let query = supabase?.from('treatments')?.select(`
          *,
          category:treatment_categories(name, slug)
        `)?.eq('status', 'published');

      if (filters?.categoryId) {
        query = query?.eq('category_id', filters?.categoryId);
      }

      if (filters?.isFlagship) {
        query = query?.eq('is_flagship', true);
      }

      if (filters?.isFeatured) {
        query = query?.eq('is_featured', true);
      }

      const { data, error } = await query?.order('created_at', { ascending: false });

      if (error) throw error;
      return { data, error: null };
    } catch (error) {
      return { data: null, error };
    }
  },

  async getTreatmentBySlug(slug) {
    try {
      const { data, error } = await supabase?.from('treatments')?.select(`
          *,
          category:treatment_categories(name, slug),
          diet_guides(*),
          exercise_routines(*)
        `)?.eq('slug', slug)?.eq('status', 'published')?.single();

      if (error) throw error;
      return { data, error: null };
    } catch (error) {
      return { data: null, error };
    }
  },

  // Diet Guides
  async getDietGuides(treatmentId = null) {
    try {
      let query = supabase?.from('diet_guides')?.select(`
          *,
          treatment:treatments(title, slug)
        `)?.eq('status', 'published');

      if (treatmentId) {
        query = query?.eq('treatment_id', treatmentId);
      }

      const { data, error } = await query?.order('created_at', { ascending: false });

      if (error) throw error;
      return { data, error: null };
    } catch (error) {
      return { data: null, error };
    }
  },

  // Exercise Routines
  async getExerciseRoutines(treatmentId = null) {
    try {
      let query = supabase?.from('exercise_routines')?.select(`
          *,
          treatment:treatments(title, slug)
        `)?.eq('status', 'published');

      if (treatmentId) {
        query = query?.eq('treatment_id', treatmentId);
      }

      const { data, error } = await query?.order('created_at', { ascending: false });

      if (error) throw error;
      return { data, error: null };
    } catch (error) {
      return { data: null, error };
    }
  },

  // Appointments
  async createAppointment(appointmentData) {
    try {
      const { data, error } = await supabase?.from('appointments')?.insert([appointmentData])?.select()?.single();

      if (error) throw error;
      return { data, error: null };
    } catch (error) {
      return { data: null, error };
    }
  },

  async getUserAppointments(userId) {
    try {
      // First, get the client_id for this user
      const { data: clientData } = await supabase
        ?.from("clients")
        ?.select("id")
        ?.eq("user_id", userId)
        ?.single();

      if (!clientData) {
        // No client record found for this user
        return { data: [], error: null };
      }

      const { data, error } = await supabase?.from('appointments')?.select(`
          *,
          treatment:treatments(title, slug),
          client:clients!client_id(full_name, email, phone),
          doctor:user_profiles!appointments_doctor_id_fkey(full_name, email)
        `)?.eq('client_id', clientData?.id)?.order('created_at', { ascending: false });

      if (error) throw error;
      return { data, error: null };
    } catch (error) {
      return { data: null, error };
    }
  },

  // Patient Cases & Testimonials
  async getPatientCases(treatmentId = null) {
    try {
      let query = supabase?.from('patient_cases')?.select(`
          *,
          treatment:treatments(title, slug)
        `)?.eq('is_published', true);

      if (treatmentId) {
        query = query?.eq('treatment_id', treatmentId);
      }

      const { data, error } = await query?.order('created_at', { ascending: false });

      if (error) throw error;
      return { data, error: null };
    } catch (error) {
      return { data: null, error };
    }
  },

  // Blog Posts
  async getBlogPosts(filters = {}) {
    try {
      let query = supabase?.from('blog_posts')?.select(`
          *,
          author:user_profiles(full_name)
        `)?.eq('status', 'published');

      if (filters?.isFeatured) {
        query = query?.eq('is_featured', true);
      }

      if (filters?.treatmentId) {
        query = query?.contains('treatment_ids', [filters?.treatmentId]);
      }

      const { data, error } = await query?.order('published_at', { ascending: false });

      if (error) throw error;
      return { data, error: null };
    } catch (error) {
      return { data: null, error };
    }
  },

  async getBlogPostBySlug(slug) {
    try {
      const { data, error } = await supabase?.from('blog_posts')?.select(`
          *,
          author:user_profiles(full_name)
        `)?.eq('slug', slug)?.eq('status', 'published')?.single();

      if (error) throw error;
      return { data, error: null };
    } catch (error) {
      return { data: null, error };
    }
  },

  // User Profile Management
  async updateUserProfile(userId, profileData) {
    try {
      const { data, error } = await supabase?.from('user_profiles')?.update(profileData)?.eq('id', userId)?.select()?.single();

      if (error) throw error;
      return { data, error: null };
    } catch (error) {
      return { data: null, error };
    }
  },

  // ========================================
  // Treatment Center Pages - NEW METHODS
  // ========================================

  /**
   * Get complete treatment page data by slug
   * Merges treatment data with treatment_center_pages content
   * 
   * @param {string} slug - Treatment slug
   * @returns {Promise<Object>} Combined treatment and center page data
   */
  async getCompleteTreatmentPage(slug) {
    try {
      // Fetch both treatment and treatment center page data - FIXED: Use maybeSingle() to handle missing treatments
      const { data: treatment, error: treatmentError } = await supabase?.from('treatments')?.select(`
          *,
          category:treatment_categories(*)
        `)?.eq('slug', slug)?.maybeSingle(); // ✅ CHANGE: maybeSingle() instead of single()

      // Handle case where treatment doesn't exist
      if (treatmentError) throw treatmentError;
      if (!treatment) {
        return {
          data: null,
          error: { message: `Treatment with slug "${slug}" not found`, code: 'TREATMENT_NOT_FOUND' }
        };
      }

      // Fetch treatment center page content - FIXED: Use maybeSingle() instead of single()
      const { data: centerPage, error: centerError } = await supabase
        ?.from('treatment_center_pages')
        ?.select('*')
        ?.eq('treatment_id', treatment?.id)
        ?.maybeSingle(); // ✅ CHANGE: maybeSingle() returns null instead of throwing error for 0 rows

      // If center page doesn't exist or has empty data, provide default structure
      if (!centerPage || centerError) {
        console.warn(`No treatment center page found for ${slug}, using treatment data only`);
        return {
          data: {
            ...treatment,
            // Provide default structure for missing center page
            centerPage: {
              heroTitle: treatment?.title,
              heroSubtitle: treatment?.short_description,
              overview: treatment?.long_description,
              benefits: [],
              causes: [],
              symptoms: [],
              ayurvedaView: treatment?.ayurvedic_explanation,
              panchakarma: {},
              assessmentInfo: null,
              treatmentProcess: [],
              faqs: [],
              whyUs: [],
              seo: {
                title: treatment?.seo_title,
                description: treatment?.seo_description,
                keywords: treatment?.tags?.join(', ')
              }
            }
          }
        };
      }
      // Convert database fields to camelCase for frontend
      const centerPageCamelCase = {
        heroTitle: centerPage?.hero_title || treatment?.title,
        heroSubtitle: centerPage?.hero_subtitle || treatment?.short_description,
        overview: centerPage?.overview || treatment?.long_description,
        benefits: centerPage?.benefits || [],
        causes: centerPage?.causes || [],
        symptoms: centerPage?.symptoms || [],
        ayurvedaView: centerPage?.ayurveda_view || treatment?.ayurvedic_explanation,
        panchakarma: centerPage?.panchakarma || {},
        assessmentInfo: centerPage?.assessment_info || null,
        treatmentProcess: centerPage?.treatment_process || [],
        faqs: centerPage?.faqs || [],
        whyUs: centerPage?.why_us || [],
        seo: centerPage?.seo || {
          title: treatment?.seo_title,
          description: treatment?.seo_description,
          keywords: treatment?.tags?.join(', ')
        }
      };

      return {
        data: {
          ...treatment,
          centerPage: centerPageCamelCase
        }
      };
    } catch (error) {
      console.error('Error fetching complete treatment page:', error);
      throw error;
    }
  },

  /**
   * Get all treatment center pages (admin use)
   * @returns {Promise} Array of treatment center pages
   */
  async getAllTreatmentCenterPages() {
    try {
      const { data, error } = await supabase
        ?.from('treatment_center_pages')
        ?.select(`
          *,
          treatment:treatments(id, title, slug, status)
        `)
        ?.order('created_at', { ascending: false });

      if (error) throw error;
      return { data, error: null };
    } catch (error) {
      return { data: null, error };
    }
  },

  /**
   * Update treatment center page content
   * @param {string} pageId - Treatment center page ID
   * @param {Object} contentData - Page content to update
   * @returns {Promise} Updated page data
   */
  async updateTreatmentCenterPage(pageId, contentData) {
    try {
      const { data, error } = await supabase
        ?.from('treatment_center_pages')
        ?.update(contentData)
        ?.eq('id', pageId)
        ?.select()
        ?.single();

      if (error) throw error;
      return { data, error: null };
    } catch (error) {
      return { data: null, error };
    }
  },

  /**
   * Create or update treatment center page
   * @param {string} treatmentId - Treatment ID
   * @param {Object} pageData - Complete page data
   * @returns {Promise} Created/Updated page data
   */
  async upsertTreatmentCenterPage(treatmentId, pageData) {
    try {
      const { data, error } = await supabase
        ?.from('treatment_center_pages')
        ?.upsert(
          { treatment_id: treatmentId, ...pageData },
          { onConflict: 'treatment_id' }
        )
        ?.select()
        ?.single();

      if (error) throw error;
      return { data, error: null };
    } catch (error) {
      return { data: null, error };
    }
  },

  /**
   * Auto-generate placeholder pages for treatments without content
   * @returns {Promise} Result of generation
   */
  async autoGeneratePlaceholderPages() {
    try {
      const { data, error } = await supabase?.rpc('autogenerate_treatment_center_pages');

      if (error) throw error;
      return { data: { success: true }, error: null };
    } catch (error) {
      return { data: null, error };
    }
  }
};